import { FC } from 'react'

import cn from 'classnames'

import {
  ExitFullscreenIcon,
  FullscreenIcon,
  PauseIcon,
  PlayIcon,
  SpeakerIcon
} from '@innoscripta/icons'

import { PlaybackSpeedControl } from './PlaybackSpeedControl'
import styles from './VideoPlayer.module.scss'
import {
  formatVideoTime,
  getRangeStyle,
  volumeToSliderPosition
} from './VideoPlayer.utils'
import { VideoPlayerControlsProps } from './VideoPlayerControls.types'

export const VideoPlayerControls: FC<VideoPlayerControlsProps> = ({
  areVisible,
  currentTime,
  duration,
  features,
  isFullscreen,
  isMuted,
  isPlaying,
  isSpeedMenuOpen,
  labels,
  playbackRate,
  setIsSpeedMenuOpen,
  volume,
  onKeepVisible,
  onPlaybackRateChange,
  onReveal,
  onSeek,
  onToggleFullscreen,
  onToggleMute,
  onTogglePlayback,
  onVolumeInput
}) => {
  const seekPercentage = duration > 0 ? (currentTime / duration) * 100 : 0
  const volumeSliderPosition = isMuted ? 0 : volumeToSliderPosition(volume)
  const volumePercentage = volumeSliderPosition * 100

  return (
    <div
      className={cn(styles.controls, {
        [styles.controlsHidden]: !areVisible
      })}
      data-controls-visible={areVisible}
      onMouseEnter={onKeepVisible}
      onMouseLeave={onReveal}
    >
      {features.seek && (
        <input
          className={cn(styles.range, styles.seekRange)}
          type='range'
          min={0}
          max={duration || 0}
          step={0.1}
          value={Math.min(currentTime, duration || 0)}
          style={getRangeStyle(seekPercentage)}
          aria-label={labels.seek}
          aria-valuetext={`${formatVideoTime(currentTime)} / ${formatVideoTime(
            duration
          )}`}
          onChange={event => onSeek(Number(event.currentTarget.value))}
        />
      )}

      <div className={styles.controlRow}>
        <div className={styles.primaryControls}>
          {features.playPause && (
            <button
              type='button'
              className={styles.controlButton}
              onClick={onTogglePlayback}
              aria-label={isPlaying ? labels.pause : labels.play}
            >
              {isPlaying ? (
                <PauseIcon size={20} fill='currentColor' aria-hidden='true' />
              ) : (
                <PlayIcon size={20} fill='currentColor' aria-hidden='true' />
              )}
            </button>
          )}

          {(features.currentTime || features.duration) && (
            <div className={styles.time} aria-live='off'>
              {features.currentTime && (
                <span>{formatVideoTime(currentTime)}</span>
              )}
              {features.currentTime && features.duration && (
                <span aria-hidden='true'>/</span>
              )}
              {features.duration && <span>{formatVideoTime(duration)}</span>}
            </div>
          )}
        </div>

        <div className={styles.secondaryControls}>
          {features.volume && (
            <div className={styles.volumeControls}>
              <button
                type='button'
                className={styles.controlButton}
                onClick={onToggleMute}
                aria-label={
                  isMuted || volume === 0 ? labels.unmute : labels.mute
                }
              >
                <span
                  className={cn(styles.volumeIcon, {
                    [styles.volumeIconMuted]: isMuted || volume === 0
                  })}
                  aria-hidden='true'
                >
                  <SpeakerIcon size={20} fill='currentColor' />
                </span>
              </button>
              <input
                className={cn(styles.range, styles.volumeRange)}
                type='range'
                min={0}
                max={1}
                step={0.01}
                value={volumeSliderPosition}
                style={getRangeStyle(volumePercentage)}
                aria-label={labels.volume}
                aria-valuetext={`${Math.round(volumePercentage)}%`}
                onChange={event =>
                  onVolumeInput(Number(event.currentTarget.value))
                }
              />
            </div>
          )}

          {features.playbackRate && (
            <PlaybackSpeedControl
              isOpen={isSpeedMenuOpen}
              label={labels.playbackSpeed}
              playbackRate={playbackRate}
              setIsOpen={setIsSpeedMenuOpen}
              onChange={onPlaybackRateChange}
            />
          )}

          {features.fullscreen && (
            <button
              type='button'
              className={styles.controlButton}
              onClick={onToggleFullscreen}
              aria-label={
                isFullscreen ? labels.exitFullscreen : labels.enterFullscreen
              }
            >
              {isFullscreen ? (
                <ExitFullscreenIcon
                  size={20}
                  fill='currentColor'
                  aria-hidden='true'
                />
              ) : (
                <FullscreenIcon
                  size={20}
                  fill='currentColor'
                  aria-hidden='true'
                />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
